import { Delete } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@up2d8/ui';

export const PIN_LENGTH = 6;

/**
 * Een cijfertoetsenbord voor de pincode.
 *
 * Waarom niet gewoon een invoerveld met `inputmode="numeric"`? Omdat het toestel dan
 * bepaalt hoe groot de toetsen zijn, en op de werkvloer wordt er met handschoenen getypt.
 * Deze toetsen zijn minstens 64 px hoog — ruim boven de vloereis van 48 px — en staan
 * onderaan het scherm, binnen duimbereik.
 *
 * Het veld zelf blijft bestaan voor schermlezers en voor wachtwoordmanagers; de bolletjes
 * zijn alleen een weergave.
 */
export function PinPad({
  value,
  onChange,
  label,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const press = (digit: string) => {
    if (value.length >= PIN_LENGTH) return;
    onChange(value + digit);
  };

  return (
    <div className="flex flex-col gap-element">
      <label className="flex flex-col gap-2">
        <span className="text-content font-medium text-text-primary">{label}</span>
        <input
          className="sr-only"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={PIN_LENGTH}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))}
        />
        <span className="flex justify-center gap-3" aria-hidden>
          {Array.from({ length: PIN_LENGTH }, (_, index) => (
            <span
              key={index}
              className={
                index < value.length
                  ? 'h-4 w-4 rounded-full bg-brand-primary'
                  : 'h-4 w-4 rounded-full border-2 border-border'
              }
            />
          ))}
        </span>
      </label>

      <div className="grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <Button
            key={digit}
            type="button"
            variant="secondary"
            className="h-16 text-xl"
            disabled={disabled}
            onClick={() => press(digit)}
          >
            {digit}
          </Button>
        ))}
        <Button
          type="button"
          variant="ghost"
          className="h-16"
          disabled={disabled || value.length === 0}
          onClick={() => onChange('')}
        >
          {t('common.clear')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="h-16 text-xl"
          disabled={disabled}
          onClick={() => press('0')}
        >
          0
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-16"
          aria-label={t('pin.backspace')}
          disabled={disabled || value.length === 0}
          onClick={() => onChange(value.slice(0, -1))}
        >
          <Delete aria-hidden className="h-6 w-6" strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}
